import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Typography from '@mui/material/Typography';
import {
  AuthMethod,
  ModelError_Item,
  ModelError_Response,
} from '@treadinc/horizon-api-spec';
import { AxiosError, isAxiosError } from 'axios';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import AppearanceForm from '~components/UserForm/AppearanceForm';
import {
  UserForm,
  UserFormHandler,
  UserValidationDTOWithSitesDiff,
} from '~components/UserForm/UserForm';
import UserNotificationsPreferenceForm, {
  UserNotificationsPreferenceDTO,
} from '~components/UserForm/UserNotificationsPreferenceForm';
import { FormStateChangeProps } from '~formsShared/types';
import { useCompany } from '~hooks/useCompany/useCompany';
import { useResourceScopes } from '~hooks/useResourceScopes/useResourceScopes';
import { RoleItem, User } from '~hooks/useUsers/models';
import useUsers from '~hooks/useUsers/useUsers';
import SettingsPageHeader from '~pages/Settings/components/SettingsPageHeader';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';

const UserProfile = () => {
  const [userFormIsDirty, setUserFormIsDirty] = useState(false);

  const [isEditing, setIsEditing] = useState<boolean>(false);
  const { updateUser, updateUserNotificationPreferences, isUpdating } = useUsers();
  const { toasterStore, userStore } = useStores();
  const userProfileRef = useRef<UserFormHandler>(null);
  const [availableAuthMethodsForUserCompany, setAvailableAuthMethodsForUserCompany] =
    useState<AuthMethod[]>([]);
  const { getCompanyById } = useCompany();
  const { saveUserSitesDiff } = useResourceScopes();

  const [lastUpdateTimestamp, setLastUpdateTimestamp] = useState<number>();
  const [isCompanyLoading, setIsCompanyLoading] = useState(false);
  const [asyncErrors, setAsyncErrors] = useState<ModelError_Item[]>([]);

  const currentUser = useMemo(() => userStore.user, [userStore.user]);
  const onSuccess = (user?: User) => {
    const name = `${user?.firstName} ${user?.lastName}`;
    toasterStore.push(alert(t('user.user_updated', { name }), AlertTypes.success));
  };
  const onCancel = () => {
    setIsEditing(false);
    setAsyncErrors([]);
    userProfileRef.current?.resetForm();
  };
  const onUserFormStateChange = ({ isDirty }: FormStateChangeProps) => {
    setUserFormIsDirty(isDirty);
  };

  const onNotificationPreferenceChange = useCallback(
    async (userNotificationsPreferenceData: Partial<UserNotificationsPreferenceDTO>) => {
      await updateUserNotificationPreferences(
        currentUser.id,
        userNotificationsPreferenceData,
      );
      toasterStore.push(
        alert(t('administration.configuration.configuration_saved'), AlertTypes.success),
      );
    },
    [currentUser.id, updateUserNotificationPreferences],
  );

  const onSubmitCallback = async (userData: UserValidationDTOWithSitesDiff) => {
    const userCompanyId = userStore.userCompany.id;

    await saveUserSitesDiff(currentUser.id, userData.sitesDiff, userCompanyId);

    const updatedUser = await updateUser(
      {
        id: currentUser?.id,
        ...userData,
      } as unknown as User,
      undefined,
      userCompanyId,
    );

    onSuccess(updatedUser);
    setIsEditing(false);
    setLastUpdateTimestamp(new Date().getTime());
  };

  const onSubmitForm = async () => {
    const userData = await new Promise<UserValidationDTOWithSitesDiff | undefined>(
      (resolve) => {
        userProfileRef.current?.submit((data) => resolve(data));
      },
    );

    if (userData) {
      setAsyncErrors([]);

      try {
        await onSubmitCallback(userData);
      } catch (error) {
        if (isAxiosError(error) && error.response?.status === 422) {
          const e = error as AxiosError<ModelError_Response>;
          setAsyncErrors(e.response?.data.error.errors || []);
        }

        // Error toast is handled in the hook.
      }
    }
  };

  useEffect(() => {
    if (currentUser?.company?.id && !availableAuthMethodsForUserCompany.length) {
      const fetchCompanyAuthMethods = async () => {
        setIsCompanyLoading(true);
        try {
          const company = await getCompanyById({ id: String(currentUser.company?.id) });
          if (company) {
            setAvailableAuthMethodsForUserCompany(company.authMethods);
          }
        } finally {
          setIsCompanyLoading(false);
        }
      };

      fetchCompanyAuthMethods();
    }
  }, [currentUser?.company?.id, availableAuthMethodsForUserCompany.length]);

  if (isCompanyLoading && !availableAuthMethodsForUserCompany.length) {
    return <Typography>{t('actions.loading')}</Typography>;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        flexDirection: 'column',
        maxWidth: '800px',
      }}
    >
      <SettingsPageHeader
        title={t('page_headings.user_profile')}
        description={t('settings.page_descriptions.user_profile')}
      />
      <Box
        className="sticky-top"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          py: 2,
          mt: -2,
          backgroundColor: 'background.paper',
        }}
      >
        <Box
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'end', gap: 1 }}
        >
          {isEditing ? (
            <SmallButton color="secondary" onClick={onCancel} variant="outlined">
              {t('actions.cancel')}
            </SmallButton>
          ) : (
            <SmallButton
              color="brandV2Yellow"
              disabled={isUpdating || isCompanyLoading}
              onClick={() => setIsEditing((prev) => !prev)}
            >
              {t('actions.edit')}
            </SmallButton>
          )}

          <SmallLoadingButton
            color="brandV2Yellow"
            disabled={isUpdating || isCompanyLoading || !userFormIsDirty}
            loading={isUpdating}
            onClick={onSubmitForm}
          >
            {t('actions.update')}
          </SmallLoadingButton>
        </Box>
      </Box>
      {currentUser?.id && (
        <>
          <Card sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {t('settings.user_profile.basic_information')}
            </Typography>
            <UserForm
              key={lastUpdateTimestamp}
              ref={userProfileRef}
              disableUserRolesField
              editable={isEditing}
              userRolesOptions={currentUser?.userRoles as RoleItem[]}
              defaultUser={currentUser}
              errors={asyncErrors}
              onFormStateChange={onUserFormStateChange}
              availableAuthMethods={availableAuthMethodsForUserCompany}
              source="user_profile"
            />
          </Card>

          <Card sx={{ p: 2, mb: 2 }}>
            <UserNotificationsPreferenceForm
              onPreferenceChange={onNotificationPreferenceChange}
              user={currentUser}
            />
          </Card>

          <Card sx={{ p: 2, mb: 2 }}>
            <AppearanceForm user={currentUser} />
          </Card>
        </>
      )}
    </Box>
  );
};
export default observer(UserProfile);
